<template>
  <ProductFilterGroup
    v-if="brandFilterEnabled && brandOptions.length"
    label="Brand"
    :collapsible="collapsible"
  >
    <USelectMenu
      v-model="selectedBrandIds"
      :items="brandOptions"
      value-key="value"
      label-key="label"
      placeholder="Caută brand"
      searchable
      multiple
      class="w-full"
      @update:model-value="emit('filters-change')"
    />
  </ProductFilterGroup>

  <ProductFilterGroup
    v-for="(attribute, index) in filterableAttributes"
    :key="attribute.id"
    :label="attribute.name"
    :collapsible="collapsible"
    :default-open="index < 3 || !!attributeSelections[attribute.id]?.length"
  >
    <USelectMenu
      v-model="attributeSelections[attribute.id]"
      :items="attributeValueOptions(attribute)"
      value-key="value"
      label-key="label"
      :placeholder="`Caută ${attribute.name.toLowerCase()}`"
      searchable
      multiple
      class="w-full"
      @update:model-value="emit('attribute-change')"
    />
  </ProductFilterGroup>

  <ProductFilterGroup
    v-if="collapsible"
    label="Preț (RON)"
    collapsible
  >
    <div class="grid grid-cols-2 gap-2">
      <UInput
        v-model.number="localFilters.minPrice"
        type="number"
        min="0"
        placeholder="Minim"
        aria-label="Preț minim (RON)"
        @change="emit('filters-change')"
      />
      <UInput
        v-model.number="localFilters.maxPrice"
        type="number"
        min="0"
        placeholder="Maxim"
        aria-label="Preț maxim (RON)"
        @change="emit('filters-change')"
      />
    </div>
  </ProductFilterGroup>

  <template v-else>
    <UFormField label="Preț minim (RON)">
      <UInput
        v-model.number="localFilters.minPrice"
        type="number"
        min="0"
        placeholder="0"
        @change="emit('filters-change')"
      />
    </UFormField>

    <UFormField label="Preț maxim (RON)">
      <UInput
        v-model.number="localFilters.maxPrice"
        type="number"
        min="0"
        placeholder="10000"
        @change="emit('filters-change')"
      />
    </UFormField>
  </template>

  <ProductFilterGroup
    v-if="availabilityOptions.length"
    label="Disponibilitate"
    :collapsible="collapsible"
  >
    <USelectMenu
      v-model="selectedAvailabilityIds"
      :items="availabilityOptions"
      value-key="value"
      label-key="label"
      placeholder="Status livrare"
      multiple
      class="w-full"
      @update:model-value="emit('filters-change')"
    />
  </ProductFilterGroup>

  <UCheckbox
    v-if="refurbishedCount != null"
    v-model="localFilters.refurbishedOnly"
    :label="refurbishedLabel"
    @update:model-value="emit('filters-change')"
  />

  <UCheckbox
    v-if="onSaleFilterEnabled"
    v-model="localFilters.onSale"
    :label="onSaleLabel"
    @update:model-value="emit('filters-change')"
  />

  <UCheckbox
    v-if="stockManagementEnabled"
    v-model="localFilters.inStock"
    label="Doar produse în stoc"
    @update:model-value="emit('filters-change')"
  />

  <UButton
    v-if="!hideReset"
    label="Resetează filtrele"
    variant="solid"
    color="neutral"
    block
    @click="emit('reset')"
  />
</template>

<script setup lang="ts">
import type { ProductAttribute, ProductFacets, ProductFilterState } from '~/types'
import { PRODUCT_FILTER_SETTING_KEYS } from '~/utils/productFilters'

const props = defineProps<{
  localFilters: ProductFilterState
  attributeSelections: Record<number, number[]>
  filterableAttributes: ProductAttribute[]
  attributeCounts?: Record<number, Record<number, number>>
  facetsReady?: boolean
  hideReset?: boolean
  brandFacets?: ProductFacets['brands']
  onSaleCount?: number
  availabilityFacets?: ProductFacets['availability']
  refurbishedCount?: number
}>()

const emit = defineEmits<{
  'filters-change': []
  'attribute-change': []
  reset: []
}>()

const { stockManagementEnabled, settings } = useSiteSettings()

const brandFilterEnabled = computed(() => settings.value[PRODUCT_FILTER_SETTING_KEYS.brand] === 'true')
const onSaleFilterEnabled = computed(() => settings.value[PRODUCT_FILTER_SETTING_KEYS.onSale] === 'true')
const collapsible = computed(() => settings.value[PRODUCT_FILTER_SETTING_KEYS.collapsible] === 'true')

const onSaleLabel = computed(() =>
  props.facetsReady && props.onSaleCount != null ? `La reducere (${props.onSaleCount})` : 'La reducere',
)

const refurbishedLabel = computed(() =>
  props.refurbishedCount != null ? `Resigilate (${props.refurbishedCount})` : 'Resigilate',
)

const selectedAvailabilityIds = computed<number[]>({
  get: () => props.localFilters.availabilityStatusIds ?? [],
  set: (ids) => {
    props.localFilters.availabilityStatusIds = ids.length ? ids : undefined
  },
})

const availabilityOptions = computed(() => {
  const selected = new Set(props.localFilters.availabilityStatusIds ?? [])
  return (props.availabilityFacets ?? [])
    .filter((status) => status.count > 0 || selected.has(status.id))
    .map((status) => ({ label: `${status.name} (${status.count})`, value: status.id }))
})

const selectedBrandIds = computed<number[]>({
  get: () => props.localFilters.brandIds ?? [],
  set: (ids) => {
    props.localFilters.brandIds = ids.length ? ids : undefined
  },
})

const brandOptions = computed(() => {
  const selected = new Set(props.localFilters.brandIds ?? [])
  return (props.brandFacets ?? [])
    .filter((brand) => brand.count > 0 || selected.has(brand.id))
    .map((brand) => ({ label: `${brand.name} (${brand.count})`, value: brand.id }))
})

function attributeValueOptions(attribute: ProductAttribute) {
  const counts = props.attributeCounts?.[attribute.id]
  const selected = new Set(props.attributeSelections[attribute.id] || [])
  return (attribute.values || [])
    .map((value) => {
      const count = counts?.[value.id] ?? 0
      return {
        label: props.facetsReady ? `${value.name} (${count})` : value.name,
        value: value.id,
        available: count > 0,
        selected: selected.has(value.id),
      }
    })
    .filter((option) => {
      if (!props.facetsReady) return true
      return option.available || option.selected
    })
    .map(({ label, value }) => ({ label, value }))
}
</script>
